import { Suspense, lazy, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bell, MapPin, WifiOff, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import logo from "@/assets/logo-white.png";
import { StoreBadges } from "@/components/marketing/StoreBadges";
import { detectMobilePlatform } from "@/lib/appStoreLinks";

// The QR only renders for desktop visitors, so its ~6 kB stay out of the
// first load for everyone else — including every producer and every
// marketing visitor, who share this app's single bundle.
const QRCodeSVG = lazy(() =>
  import("qrcode.react").then((module) => ({ default: module.QRCodeSVG })),
);

/**
 * What a crew member sees in a browser, at `/crew-login`.
 *
 * Crew used to have a whole web app here. They now work in the native app,
 * which is the only place background location, push notifications and
 * offline access can actually work — so the browser's job is reduced to
 * pointing them at it.
 *
 * On a phone this page is rarely reached at all: `/crew-login` is a
 * universal link, so an invite or verification email opens the app
 * directly when it is installed. The page therefore assumes it is mostly
 * being read on a laptop, and leads with a QR code.
 */
const GetCrewApp = () => {
  const [searchParams] = useSearchParams();

  // Read once at mount. An effect would repaint the page after first
  // paint — dropping the QR and a badge on a phone, visibly.
  const [platform] = useState(() => detectMobilePlatform());
  const onDesktop = platform === "other";

  // The verification email lands here with ?verified=true, and an invite
  // with ?briefing=<id>. Both arrive expecting something specific; saying
  // nothing reads as a dead end.
  const justVerified = searchParams.get("verified") === "true";
  const hasBriefing = !!searchParams.get("briefing");

  // The QR points back at this page rather than at one store: /crew-login
  // is a universal link, so a phone that scans it opens the app when it has
  // it, and otherwise lands here again — where the badge for its own
  // platform is the one being shown. A QR straight to the App Store would
  // send Android users somewhere they cannot install anything. The query
  // string rides along: the app reads ?briefing= and opens that production.
  const pageUrl = `${window.location.origin}/crew-login${window.location.search}`;

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-lg space-y-8">
        <div className="flex flex-col items-center gap-6 text-center">
          <Link to="/">
            <img src={logo} alt="PROduce" width={160} height={40} className="h-10 w-auto" />
          </Link>

          {justVerified && (
            <div className="flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Email verified
            </div>
          )}

          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-foreground">Get the PROduce Crew app</h1>
            <p className="text-muted-foreground">
              {hasBriefing
                ? "Your production is waiting in the app. Sign in there with the email address you were invited on — there is nothing to set up here."
                : "Your schedule, briefing and live updates live in the app. Sign in there with the email address you were invited on — there is nothing to set up here."}
            </p>
          </div>
        </div>

        <Card className="space-y-6 p-6">
          {onDesktop && (
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-xl bg-white p-3">
                <Suspense fallback={<Skeleton className="h-[148px] w-[148px]" />}>
                  <QRCodeSVG
                    value={pageUrl}
                    size={148}
                    level="M"
                    title="QR code that opens the PROduce Crew app on your phone"
                  />
                </Suspense>
              </div>
              <p className="text-sm text-muted-foreground">
                Scan with your phone&apos;s camera, or search for{" "}
                <span className="font-medium text-foreground">PROduce Crew</span> in your app store.
              </p>
            </div>
          )}

          <StoreBadges size="lg" />
        </Card>

        {/* Why it is the app and not a web page — the honest reason, not a
            sales pitch: these three genuinely cannot work in a browser. */}
        <section aria-labelledby="why-the-app" className="space-y-3">
          <h2 id="why-the-app" className="text-center text-sm font-medium text-foreground">
            Why the app, and not this browser
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-3">
              <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              Push notifications the moment your schedule changes
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              Location sharing with your producer while you are on site
            </li>
            <li className="flex items-start gap-3">
              <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              Your schedule and briefing offline, where the signal drops
            </li>
          </ul>
        </section>

        <p className="text-center text-sm text-muted-foreground">
          Are you a producer?{" "}
          <Link to="/producer-login" className="font-medium text-primary hover:underline">
            Sign in here
          </Link>
        </p>
      </div>
    </main>
  );
};

export default GetCrewApp;
